import React, { useState, useMemo } from 'react';
import { Box, IconButton, Dialog, DialogContent } from '@mui/material';
import { Close as CloseIcon, ZoomIn as ZoomInIcon } from '@mui/icons-material';
import { normalizeMediaUrl } from '../../utils/mediaUrl';

export interface MediaPreviewProps {
  type: 'image' | 'video' | 'audio' | 'text';
  url: string;
  alt?: string;
  controls?: boolean;
  autoPlay?: boolean;
  maxHeight?: string;
  allowFullscreen?: boolean;
  variant?: 'positive' | 'negative' | 'neutral' | 'purple';
}

export const MediaPreview: React.FC<MediaPreviewProps> = ({
  type,
  url: rawUrl,
  alt = 'Media preview',
  controls = true,
  autoPlay = false,
  maxHeight = '400px',
  allowFullscreen = true,
  variant = 'neutral',
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Normalize URL to work across environments
  const url = useMemo(() => normalizeMediaUrl(rawUrl), [rawUrl]);

  const renderMedia = (isDialog = false) => {
    const className = isDialog ? 'media-preview-fullscreen' : 'media-preview-content';

    switch (type) {
      case 'image':
        return (
          <img
            src={url}
            alt={alt}
            className={className}
            style={!isDialog ? { maxHeight } : undefined}
          />
        );

      case 'video':
        return (
          <video
            src={url}
            controls={controls}
            autoPlay={autoPlay}
            className={className}
            style={!isDialog ? { maxHeight } : undefined}
          >
            Your browser does not support the video tag.
          </video>
        );

      case 'audio':
        return (
          <audio
            src={url}
            controls={controls}
            autoPlay={autoPlay}
            className={className}
          >
            Your browser does not support the audio tag.
          </audio>
        );

      case 'text':
        return (
          <Box
            className={className}
            style={!isDialog ? { maxHeight, overflowY: 'auto' } : undefined}
          >
            <iframe
              src={url}
              title={alt}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          </Box>
        );

      default:
        return <Box className={className}>Unsupported media type</Box>;
    }
  };

  return (
    <>
      <Box className={`media-preview-container media-preview-${variant}`}>
        {renderMedia()}
        
        {allowFullscreen && (type === 'image' || type === 'video') && (
          <IconButton
            className="media-preview-fullscreen-button"
            onClick={() => setIsFullscreen(true)}
            size="small"
          >
            <ZoomInIcon />
          </IconButton>
        )}
      </Box>

      {allowFullscreen && (
        <Dialog
          open={isFullscreen}
          onClose={() => setIsFullscreen(false)}
          maxWidth={false}
          className="media-preview-dialog"
        >
          <IconButton
            className="media-preview-close-button"
            onClick={() => setIsFullscreen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
          <DialogContent>
            {renderMedia(true)}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
